import { type LucideIcon, Search, X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils.ts";
import { PanelBreadcrumb } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "../../components/ui/button.tsx";
import { Input } from "../../components/ui/input.tsx";
import { useMediaQuery } from "../../hooks/useMediaQuery.tsx";

/**
 * The mobile site header's contract with the active panel. Settings.tsx owns
 * all of it: `slotEl` is a container in its drilled-in header row that panel
 * controls portal into; `searchSlotEl` replaces the title area while a panel's
 * search mode is active (it exists only then); `setTitleOverride` lets a child
 * screen (a routine's detail page, say) replace the tab label up there;
 * `setSearchActive` is how a panel asks for the search takeover. Null / no-op
 * on desktop and on the mobile settings list, where no panel is drilled in.
 */
export const SettingsHeaderSlotContext = createContext<{
  /**
   * True only under Settings' provider. Distinguishes "Settings chrome exists
   * but no panel is drilled in" (null slots, render nothing) from a host with
   * no chrome at all (the standalone /connectors route), where the header
   * falls back to an in-panel controls row.
   */
  hasChrome: boolean;
  slotEl: HTMLElement | null;
  searchSlotEl: HTMLElement | null;
  /**
   * Mobile only: sits immediately after the drilled-in title, so a child
   * screen's return link reads as an aside to it rather than spending a
   * second line of the panel on itself.
   */
  titleSlotEl: HTMLElement | null;
  /**
   * Desktop's shared bar: the panel's whole header row portals here, beside
   * the Settings title, instead of spending a row of the page on it. Null
   * (loading states, standalone hosts) renders the row in place.
   */
  desktopSlotEl: HTMLElement | null;
  setTitleOverride: (title: string | null) => void;
  setSearchActive: (active: boolean) => void;
}>({
  hasChrome: false,
  slotEl: null,
  searchSlotEl: null,
  titleSlotEl: null,
  desktopSlotEl: null,
  setTitleOverride: () => {},
  setSearchActive: () => {},
});

export type SettingsPanelAction = {
  /** Desktop button text; doubles as the mobile icon button's aria-label. */
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  statsigName: string;
};

export type SettingsPanelReturn = {
  /** The link's text. Parentheses are drawn around it, outside the button. */
  label: string;
  onClick: () => void;
  statsigName: string;
};

export type SettingsPanelSearch = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  statsigName: string;
};

/**
 * The one-line page description that sits directly under the header row,
 * shared by every settings panel so the pages introduce themselves in one
 * voice and one style.
 */
export const panelDescriptionClass = "text-sm text-muted-foreground mb-6";

/**
 * Standard top area for a settings panel, top-level and child screens alike.
 *
 * On desktop it renders one header row holding everything: the title, then
 * right-aligned, the search input, the filter control, and the page's actions.
 * The row never wraps. As it narrows the search input shrinks to a floor
 * first; if the row still overflows, every control snaps to its icon form
 * (the same set the mobile nav bar shows) until there is room again.
 *
 * On mobile it renders no in-panel row at all: the drilled-in site header is
 * the one bar, the way a native nav stack works. The title is pushed up into
 * it, the chrome chevron is the only back affordance, and every control
 * collapses to an icon beside the title. Search becomes an icon that swaps
 * the bar into search mode: the input takes the title's place and an X backs
 * out (clearing the query), like a native nav-bar search. Desktop's compact
 * state reuses the same search mode inline in the panel row.
 */
export const SettingsPanelHeader = ({
  title,
  search,
  filters,
  mobileFilters,
  action,
  extras,
  onBack,
  parentLabel,
  backStatsigName = "settings-panel-back",
  returnAction,
  actions,
  mobileActions,
  toolsKey = "",
  hideTitle = false,
}: {
  title: string;
  /**
   * The hosting chrome already shows the title (the standalone /connectors
   * route's site header), so skip the desktop h2 — it would read twice.
   * `title` still feeds the mobile chrome override.
   */
  hideTitle?: boolean;
  /** Controlled search box: an input in the desktop row, nav-bar search mode on mobile. */
  search?: SettingsPanelSearch;
  /** Filter control for the desktop row (a labeled dropdown showing the applied value). */
  filters?: ReactNode;
  /** Icon form of the filter control, for the mobile nav bar and the compact desktop row. */
  mobileFilters?: ReactNode;
  /** The panel's primary action: a labeled button on desktop, an icon in the mobile header. */
  action?: SettingsPanelAction;
  /** Extra controls rendered before the action in both placements. */
  extras?: ReactNode;
  /**
   * Child screens: navigates up to the parent list. With `parentLabel`, the
   * desktop row renders a breadcrumb — parent title as the link, a forward
   * chevron, then this screen's title — instead of stacking a second back
   * chevron under the chrome's. Mobile relies on the chrome chevron.
   */
  onBack?: () => void;
  /** The parent list's title, e.g. "Routines"; the clickable half of the breadcrumb. */
  parentLabel?: string;
  backStatsigName?: string;
  /**
   * Labelled secondary exit — the way back to whatever linked here, distinct
   * from the breadcrumb's way up. Passed as ingredients rather than markup so
   * the header owns its presentation, the way `action` already does. Reads as
   * a parenthesised aside to the title: inline on the desktop row, portalled
   * beside the drilled-in title on mobile.
   *
   * It therefore needs a title to sit beside, and is dropped by the two hosts
   * that have none — `hideTitle`, and mobile outside Settings chrome. Those own
   * their own way back.
   */
  returnAction?: SettingsPanelReturn;
  /** Custom action cluster for the desktop row (form screens with stateful buttons). */
  actions?: ReactNode;
  /** Custom action cluster for the mobile nav bar, when the desktop one doesn't translate. */
  mobileActions?: ReactNode;
  /**
   * The compact row retries the full layout when the set of tools changes,
   * but it cannot see INSIDE a slot: a control that appears or disappears
   * within `filters` (Contacts' account picker exists only with multiple
   * accounts) changes the layout's width without changing any slot's
   * presence. Panels with such variable-width tools fold that state in here
   * so the retry still fires.
   */
  toolsKey?: string;
}) => {
  const isDesktop = useMediaQuery("md");
  const {
    hasChrome,
    slotEl,
    searchSlotEl,
    titleSlotEl,
    desktopSlotEl,
    setTitleOverride,
    setSearchActive,
  } = useContext(SettingsHeaderSlotContext);
  const [searchOpen, setSearchOpen] = useState(false);
  // Desktop-only: the row overflowed with the full controls, so it shows the
  // icon forms until it is wide enough again.
  const [compact, setCompact] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const clusterRef = useRef<HTMLDivElement | null>(null);
  // The width at which the full row last overflowed, plus a buffer — retrying
  // the full layout below this would just bounce straight back to compact.
  const expandAtRef = useRef(0);
  const ActionIcon = action?.icon;

  // Mobile: the chrome header shows this panel's title instead of the tab
  // label. For top-level panels the two are identical, so the override only
  // becomes visible on child screens.
  useEffect(() => {
    if (isDesktop) return;
    setTitleOverride(title);
    return () => setTitleOverride(null);
  }, [isDesktop, title, setTitleOverride]);

  // Tell the chrome to swap its title area for the search slot. Cleanup (and
  // the desktop branch) hand the bar back on unmount or breakpoint crossing.
  const mobileSearchActive = !isDesktop && searchOpen;
  useEffect(() => {
    setSearchActive(mobileSearchActive);
    return () => setSearchActive(false);
  }, [mobileSearchActive, setSearchActive]);

  // Entering compact happens by measurement. The controls cluster is the flex
  // item that shrinks (its min-w-0 is what lets the search input give way), so
  // overflow shows up as the cluster's own deficit — content wider than its
  // box — never on the row, whose scrollWidth stays clipped to its width.
  // Runs after every render: content changes can re-overflow, not just
  // resizes. jsdom reports 0 for every metric, so tests always see full mode.
  const measureRef = useRef(() => {});
  measureRef.current = () => {
    const row = rowRef.current;
    const cluster = clusterRef.current;
    if (!isDesktop || !row) return;
    if (compact) {
      if (row.clientWidth >= expandAtRef.current) setCompact(false);
      return;
    }
    if (!cluster || cluster.children.length === 0) return;
    // The deficit is measured from child geometry, not scrollWidth: the
    // cluster is justify-end, so its unshrinkable content escapes to the
    // LEFT (over the title), and LTR scrollWidth never counts left overflow.
    const box = cluster.getBoundingClientRect();
    const first = cluster.children[0].getBoundingClientRect();
    const last = cluster.children[cluster.children.length - 1].getBoundingClientRect();
    const need = last.right - first.left;
    if (need > box.width + 1) {
      // The retry threshold is what the full layout actually needs: the row
      // as-is plus the cluster's deficit. Assigned, not ratcheted, so a stray
      // wide measurement can't wedge the row in compact forever.
      expandAtRef.current = row.clientWidth + (need - box.width) + 24;
      setCompact(true);
    }
  };
  useLayoutEffect(() => {
    measureRef.current();
  });

  // A compact row must also retry the full layout when its CONTENTS change,
  // not just when it widens: the expand threshold above was measured for the
  // old control set, so a panel that drops tools while compact (Contacts
  // scoping to one thread hides search and filters) would otherwise stay
  // wedged in icons at a width its new, smaller layout fits. Dropping out of
  // compact re-runs the overflow measurement against the current content
  // before paint, so a still-overflowing row snaps straight back.
  // The return action's LABEL, not just its presence: it shares the row with
  // the title, so a label change resizes the row without any slot appearing
  // or disappearing.
  const contentKey = [
    !!search,
    !!filters,
    !!extras,
    !!action,
    !!actions,
    title,
    returnAction?.label ?? "",
    toolsKey,
  ].join("|");
  const contentKeyRef = useRef(contentKey);
  useLayoutEffect(() => {
    if (contentKeyRef.current === contentKey) return;
    contentKeyRef.current = contentKey;
    setCompact(false);
  });

  // Resizes don't re-render React on their own, so re-measuring is wired to
  // both size signals: a ResizeObserver for container changes the window never
  // sees (the right sidebar opening), and a window resize listener, because
  // observer callbacks ride the rendering pipeline and a hidden or occluded
  // tab can sit through a resize without ever receiving one.
  useEffect(() => {
    const row = rowRef.current;
    if (!isDesktop || !row) return;
    const measure = () => measureRef.current();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isDesktop]);

  // The full-width input is its own search affordance; stray open state from
  // a compact or mobile stint would otherwise wedge the row in search mode.
  useEffect(() => {
    if (isDesktop && !compact && searchOpen) setSearchOpen(false);
  }, [isDesktop, compact, searchOpen]);

  const closeSearch = () => {
    search?.onChange("");
    setSearchOpen(false);
  };

  const searchInput = search ? (
    <Input
      autoFocus
      type="text"
      value={search.value}
      onChange={(e) => search.onChange(e.target.value)}
      placeholder={search.placeholder}
      className="h-9"
    />
  ) : null;

  const closeSearchButton = (
    <Button
      variant="ghost"
      size="icon"
      className="shrink-0"
      aria-label="Close search"
      onClick={closeSearch}
      data-statsig-name={search ? `${search.statsigName}-close` : "settings-search-close"}
    >
      <X className="size-5" />
    </Button>
  );

  // An aside to the title, carrying the breadcrumb's own link treatment: same
  // type scale and muted-to-foreground hover as the parent crumb, at regular
  // weight so it never reads as a second heading. It only ever renders beside a
  // title — never into `iconControls`, which mobile and the compact desktop row
  // share, and never in the two title-less branches below, since parentheses
  // with nothing to attach to read as a typo.
  //
  // The parens sit OUTSIDE the button so they are neither clickable nor lit on
  // hover; only the phrase is the link. They are explicit string children so
  // JSX cannot slip whitespace inside them.
  //
  // `shrink-0` on both surfaces makes the title the only element that gives.
  // Letting this shrink instead would make it a peer of the desktop search
  // input, so the label would clip before search reached its floor, and on
  // mobile the slot would narrow around a span that does not clip and spill
  // across the nav-bar icons. Overflow is the compact row's job, and it handles
  // it by giving the title `min-w-0`. Only the margin differs by surface: the
  // desktop row already spaces items with gap-2, wider than a character, so
  // `-ml-1` trims it back, while the mobile title has no gap and `ml-1` supplies
  // one.
  const returnControl = returnAction ? (
    <span
      className={cn(
        "flex shrink-0 items-center text-base text-muted-foreground",
        isDesktop ? "-ml-1" : "ml-1",
      )}
    >
      {"("}
      <Button
        variant="link"
        size="fit"
        onClick={returnAction.onClick}
        className="border-0 p-0 text-base font-normal text-muted-foreground no-underline hover:text-foreground hover:no-underline"
        data-statsig-name={returnAction.statsigName}
      >
        {returnAction.label}
      </Button>
      {")"}
    </span>
  ) : null;

  // The icon cluster is one set: the mobile nav bar portals it, desktop's
  // compact row renders it inline.
  const iconControls = (
    <>
      {search ? (
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={`Search: ${search.placeholder}`}
          onClick={() => setSearchOpen(true)}
          data-statsig-name={search.statsigName}
        >
          <Search className="size-5" />
        </Button>
      ) : null}
      {mobileFilters}
      {extras}
      {action && ActionIcon ? (
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={action.label}
          onClick={action.onClick}
          data-statsig-name={action.statsigName}
        >
          <ActionIcon className="size-5" />
        </Button>
      ) : null}
      {mobileActions}
    </>
  );

  if (!isDesktop) {
    // Hosts outside Settings (the standalone /connectors route) have no
    // chrome at all. Render the same controls as an in-panel row instead of
    // portaling, so the tools survive wherever the panel is mounted; opening
    // search swaps the row for its input, mirroring the nav-bar takeover.
    // Inside Settings, null slots mean "no panel drilled in" — keep rendering
    // nothing there, exactly as before.
    if (!hasChrome) {
      return (
        <div className="mb-4 flex items-center justify-end gap-2">
          {mobileSearchActive && searchInput ? (
            <div className="min-w-0 flex-1">{searchInput}</div>
          ) : null}
          {mobileSearchActive ? closeSearchButton : iconControls}
        </div>
      );
    }
    return (
      <>
        {/* Beside the chrome's title, not on a line of its own: a standalone
            row for one short link costs more vertical space than the link is
            worth on a phone. Search mode takes the title area over, so the
            link steps aside with it. */}
        {returnControl && titleSlotEl && !mobileSearchActive
          ? createPortal(returnControl, titleSlotEl)
          : null}
        {mobileSearchActive && searchInput && searchSlotEl
          ? createPortal(searchInput, searchSlotEl)
          : null}
        {slotEl
          ? createPortal(mobileSearchActive ? closeSearchButton : iconControls, slotEl)
          : null}
      </>
    );
  }

  // In the settings shell the whole desktop row portals into the shared bar
  // beside the Settings title; without a slot (loading states, other hosts)
  // it renders in place, where mb-6 restores the gap to the content.
  const placeRow = (row: ReactNode) => (desktopSlotEl ? createPortal(row, desktopSlotEl) : row);
  const rowClass = cn("flex h-10 items-center gap-2", !desktopSlotEl && "mb-6");

  // Compact desktop search mode: the input owns the row, like the mobile bar.
  if (compact && searchOpen && searchInput) {
    return placeRow(
      <div ref={rowRef} className={rowClass}>
        <div className="min-w-0 flex-1">{searchInput}</div>
        {closeSearchButton}
      </div>,
    );
  }

  return placeRow(
    <div ref={rowRef} className={rowClass}>
      {/* Child screens breadcrumb instead of stacking a second back chevron
          under the chrome's: the parent title is the way back, the forward
          chevron reads as "you are inside it". */}
      {onBack && parentLabel ? (
        <PanelBreadcrumb parentLabel={parentLabel} onBack={onBack} statsigName={backStatsigName} />
      ) : null}
      {/* The title outranks every tool: it never shrinks or ellipsizes while
          the row is full — the search gives way, then the cluster goes to
          icons. Only in compact may an extreme title truncate, so the icons
          themselves stay reachable. */}
      {hideTitle ? null : (
        <>
          <div className={compact ? "min-w-0" : "shrink-0"}>
            <h2 className="text-base font-semibold line-clamp-1">{title}</h2>
          </div>
          {/* A sibling of the title, never stacked under it: the row is a
              fixed h-10 and items-center, so a two-line title cell would
              centre against the breadcrumb's single line and knock the crumb
              off the title's baseline. Stays ahead of the cluster, which must
              remain the row's last child. */}
          {returnControl}
        </>
      )}
      {/* min-w-0 lets the cluster compress so the search input inside it can
          give way; the overflow that trips compact is measured as the
          cluster's own scrollWidth deficit. */}
      <div ref={clusterRef} className="ml-auto flex min-w-0 items-center justify-end gap-2">
        {compact ? (
          <div className="flex items-center gap-1">{iconControls}</div>
        ) : (
          <>
            {search ? (
              // Shrinks before anything else in the row, then stops at its
              // floor; past that the whole row snaps to the icon forms.
              <div className="relative w-56 min-w-36 shrink">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                {/* h-9 is the header-row standard: every tool matches the sm
                    button height, so pass it on any filter trigger too. */}
                <Input
                  type="text"
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  placeholder={search.placeholder}
                  className="h-9 pl-9"
                  data-statsig-name={search.statsigName}
                />
              </div>
            ) : null}
            {filters}
            {extras}
            {action && ActionIcon ? (
              <Button size="sm" onClick={action.onClick} data-statsig-name={action.statsigName}>
                <ActionIcon className="size-4" />
                {action.label}
              </Button>
            ) : null}
            {actions}
          </>
        )}
      </div>
    </div>,
  );
};
